import { Grid, Switch, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import {
  getSpotxTiersEnabledForPublisher,
  getSpotxTiersForPublisher,
  updateSpotxTiersEnabledForPublisher,
  updateSpotxTiersForPublisher,
} from '~/src/dash/api/demand-settings/spotx/spotx-tiers_methods';
import { ISpotxTiers } from '~/src/dash/api/demand-settings/spotx/spotx-tiers_models';
import { EBool } from '~/src/dash/api/types/bool';
import { SpotxTierTable } from '~/src/dash/components/demand_settings/spotx_tier_table';
import { IStore } from '~/src/dash/reducers/store';
import {
  getResellerTitle,
  isEvrideo,
  isOrka,
  isOttera,
} from '~/src/dash/resellers/utils/importResellerConfig';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import {
  errorColor,
  growGrid,
  growGridLeft,
  growGridRight,
} from '~/src/dash/utils/material-dashboard-react';
import { SnackbarV2 } from '~/src/dash/views/snackbar-view-v2';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  setSaveFunc: (handleSave: () => void) => {};
}

const _IDemandSettingsSpotx = (props: IProps) => {
  const [tiers, setTiers] = useState<ISpotxTiers[]>([]);
  const [tiersEnabled, setTiersEnabled] = useState<EBool>(EBool.EBool_FALSE);

  const snackbarV2 = new SnackbarV2();

  const fillDefaultTiers = (_tiers?: ISpotxTiers[]) => {
    if (_tiers === undefined || _tiers === null) {
      _tiers = [] as ISpotxTiers[];
      for (let i = 1; i < 11; i++) {
        // default of i (1 to 1 -> publica tier / spotx tier)
        _tiers.push({ ID: 0, PublicaTier: i, SpotxTier: i });
      }
      setTiers(_tiers);
      return;
    } else {
      for (let i = 1; i < 11; i++) {
        if (!_tiers.find((element) => element.SpotxTier === i)) {
          _tiers.push({ ID: 0, PublicaTier: i, SpotxTier: i });
        }
      }
      setTiers(_tiers);
      return;
    }
  };

  // == TIERS
  const getSpotxTiers = () => {
    getSpotxTiersForPublisher(props.session.publisher.ID).then(([_tiers, err]) => {
      if (err) {
        console.error(err);
        snackbarV2.displayMessage(err.message, true);
      } else if (_tiers) {
        fillDefaultTiers(_tiers);
      }
    });
  };

  const saveTiers = (_tiers: ISpotxTiers[]) => {
    fillDefaultTiers(_tiers);
  };

  // === TIERS ENABLED?
  const getIsSpotxTiersEnabled = () => {
    getSpotxTiersEnabledForPublisher(props.session.publisher.ID).then(([_isEnabled, err]) => {
      if (err) {
        console.error(err);
        snackbarV2.displayMessage(err.message, true);
      } else if (_isEnabled !== undefined) {
        setTiersEnabled(_isEnabled);
      }
    });
  };

  const saveIsTiersEnabled = (value: EBool) => {
    setTiersEnabled(value);
  };

  useEffect(() => {
    fillDefaultTiers(tiers);
    getIsSpotxTiersEnabled();
    getSpotxTiers();
  }, []);

  const handleSave = () => {
    // tiers
    updateSpotxTiersForPublisher(props.session.publisher.ID, tiers).then(([_tiers, err]) => {
      if (err) {
        console.error(err);
        snackbarV2.displayMessage(err.message, true);
      } else if (_tiers) {
        fillDefaultTiers(_tiers);
        snackbarV2.displayMessage('Tiers mapping saved');
      }
    });

    // enabled / disabled
    updateSpotxTiersEnabledForPublisher(props.session.publisher.ID, tiersEnabled).then(
      ([newValue, err]) => {
        if (err) {
          console.error(err);
          snackbarV2.displayMessage(err.message, true);
        } else if (newValue) {
          setTiersEnabled(newValue);
        }
      },
    );
  };

  props.setSaveFunc(handleSave);

  return (
    <div>
      <Grid container direction='row' alignItems='center' style={{ marginBottom: '10px' }}>
        <Grid item>
          <Typography style={{ fontSize: 16, fontWeight: 500 }}>Tiers Mapping Table</Typography>
        </Grid>
        <Grid item style={{ marginLeft: '20px' }}>
          {EBool.toBool(tiersEnabled) ? 'Active' : 'Inactive'}
          <Switch
            name='Enable Spotx Tiers'
            checked={EBool.toBool(tiersEnabled)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              saveIsTiersEnabled(EBool.fromBool(event.target.checked));
            }}
          />
        </Grid>
      </Grid>
      {EBool.toBool(tiersEnabled) && (
        <>
          <p>
            Assign {`${isEvrideo() || isOrka() || isOttera() ? 'an ' : 'a '}`} {getResellerTitle()}{' '}
            tier to the tier passed by demand partner in bid response. Exceptions can be set at the
            bidder level.
          </p>
          <SpotxTierTable
            firstColumnTitle='Spotx Tier'
            secondColumnTitle={`${getResellerTitle()}` + ' Tier'}
            tiers={tiers}
            saveFn={saveTiers}
          />
        </>
      )}
      {snackbarV2.component()}
    </div>
  );
};

export const DemandSettingsSpotx = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
      growGrid,
      growGridLeft,
      growGridRight,
      errorColor: {
        color: errorColor,
      },
      clampTableCell: {
        whiteSpace: 'nowrap',
        width: '1px',
      },
    }),
    router: true,
  },
  _IDemandSettingsSpotx,
);
